/* Ardor DTF Transfer Designer: the AI design drawer ("AI design" on the tool rail). Ardor Printing. New Oct 14, 2026.
Builds its form inside the drawer panel that ardor-dtf-layers.js adds ([data-afa-host]). Uses the same AI server routes as
the AI t-shirt design generator page (/pages/ai-tshirt-design-generator) and the AI sticker generator, no server change
(dtf/ai/HANDOFF.md): GET /api/quota, POST /api/generate { idea, style, visitor, email, token }, GET /img/<id> (transparent
PNG art). Same visitor id and email (localStorage wsIdeaVisitor / wsIdeaEmail) and the same invisible Cloudflare Turnstile
check, so tries are shared with those pages. Designs are kept in sessionStorage ardorDtfAiHistory like the AI page does.
"Use this design" fetches /img/<id> and hands it to ArdorDtfLayers.placeAi(), the same code the ?ai=<id> hand-off from the
AI page runs: the designer trims it (window.ArdorDtfCutout, the page's own cutout, when present) and places it as one design
at the chosen placement's preset width. Structure after scratchpad/aidrawer/sticker/ardor-sticker-ai-drawer.js. */
(function () {
  'use strict';
  var MAX = 300, MAX_DESIGN = 180, MAX_WORDS = 50;
  var STYLES = [
    ['cartoon', 'Cartoon', 'Bold outlines, bright colors'],
    ['retro', 'Retro', '70s colors, vintage badge'],
    ['kawaii', 'Kawaii', 'Cute, pastel, big eyes'],
    ['text', 'Bold text', 'Words, quotes, logos']
  ];
  var PLACES = [['front', 'Full front'], ['leftchest', 'Left chest'], ['back', 'Full back'], ['sleeve', 'Sleeve']];
  var EXAMPLES = ['A howling wolf in front of a full moon', 'A retro sunset over mountains', 'A coffee cup lifting weights', 'A bulldog in a football helmet'];
  var LOADING = ['Sketching your idea…', 'Inking the outlines…', 'Mixing the colors…', 'Cleaning up the edges…', 'Almost there…'];
  var SPARK = '<svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor" aria-hidden="true"><path d="M12 1.5c.6 5 2.8 7.8 9 9.2v.6c-6.2 1.4-8.4 4.2-9 9.2h-.6c-.6-5-2.8-7.8-9-9.2v-.6c6.2-1.4 8.4-4.2 9-9.2z"></path></svg>';

  function lget(k) { try { return window.localStorage.getItem(k); } catch (e) { return null; } }
  function lset(k, v) { try { window.localStorage.setItem(k, v); } catch (e) {} }
  function sget(k) { try { return JSON.parse(window.sessionStorage.getItem(k) || 'null'); } catch (e) { return null; } }
  function sset(k, v) { try { window.sessionStorage.setItem(k, JSON.stringify(v)); } catch (e) {} }
  function esc(t) { return String(t == null ? '' : t).replace(/[&<>"]/g, function (c) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]; }); }
  function clean(t) { return String(t || '').replace(/\s+/g, ' ').trim(); }
  function dl(name, data) {
    try { (window.dataLayer = window.dataLayer || []).push(Object.assign({ event: name }, data || {})); } catch (e) {}
    try { if (window.Shopify && Shopify.analytics && Shopify.analytics.publish) Shopify.analytics.publish(name, Object.assign({ source: 'dtf-designer' }, data || {})); } catch (e) {}
  }

  var host = null, API = '', SITEKEY = '', AI_PAGE = '/pages/ai-tshirt-design-generator';
  var visitor = lget('wsIdeaVisitor');
  if (!visitor || !/^[a-zA-Z0-9_-]{8,64}$/.test(visitor)) {
    visitor = ((window.crypto && crypto.randomUUID) ? crypto.randomUUID() : String(Date.now()) + Math.random().toString(36).slice(2)).replace(/[^a-zA-Z0-9_-]/g, '');
    lset('wsIdeaVisitor', visitor);
  }
  var email = lget('wsIdeaEmail') || '';
  var S = { style: 'cartoon', place: 'front', idea: '', ideaId: '', desc: '', words: '', images: [], selected: null, busy: false, ideasLeft: null, hasEmail: !!email, pending: null, off: false, started: false, timer: 0 };
  var ui = {};
  function $(s) { return host.querySelector(s); }
  function $$(s) { return [].slice.call(host.querySelectorAll(s)); }
  function L() { return window.ArdorDtfLayers || null; }

  // ---------- server ----------
  function api(path, body, ms) {
    var opts = body ? { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) } : {};
    var ctl = window.AbortController ? new AbortController() : null, t = 0;
    if (ctl) { opts.signal = ctl.signal; t = setTimeout(function () { ctl.abort(); }, ms || 150000); }
    return fetch(API + path, opts).then(function (r) {
      return r.json().catch(function () { return {}; }).then(function (j) {
        clearTimeout(t);
        if (!j || typeof j !== 'object') j = {};
        if (!r.ok) { if (r.status === 402 && !j.error) j.error = 'need_email'; if (!j.error) j = { error: 'server', message: 'Something went wrong on our side. Please try again.' }; j.ok = false; }
        return j;
      });
    }, function (e) {
      clearTimeout(t);
      return e && e.name === 'AbortError' ? { ok: false, error: 'timeout', message: 'This is taking longer than usual. Please try again.' } : { ok: false, error: 'network', message: 'Check your connection and try again.' };
    });
  }
  function quota() {
    return api('/api/quota?visitor=' + encodeURIComponent(visitor) + (email ? '&email=' + encodeURIComponent(email) : ''), null, 20000).then(function (q) {
      if (!q.ok) return q;
      if (q.enabled === false) { S.off = true; ui.go.disabled = true; showErr('The AI designer is taking a short break. Please try again later.'); }
      S.ideasLeft = q.ideasLeft; S.hasEmail = !!q.hasEmail; S.emailIdeas = q.emailIdeas || 10;
      setQuota();
      return q;
    });
  }

  // ---------- Turnstile: explicit render, invisible, a fresh token for every request ----------
  var tsReady = null, tsWidget = null, tsResolve = null;
  function loadTurnstile() {
    if (!SITEKEY) return Promise.resolve(false);
    if (tsReady) return tsReady;
    tsReady = new Promise(function (res) {
      if (window.turnstile && window.turnstile.render) { res(true); return; }
      window.adtTurnstileLoaded = function () { res(true); };
      var s = document.createElement('script');
      s.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit&onload=adtTurnstileLoaded';
      s.async = true; s.defer = true;
      s.onerror = function () { res(false); };
      document.head.appendChild(s);
    });
    return tsReady;
  }
  function botToken() {
    return loadTurnstile().then(function (ok) {
      if (!ok || !window.turnstile) return '';
      return new Promise(function (res) {
        tsResolve = res;
        var take = function (t) { var r = tsResolve; if (r) { tsResolve = null; r(t || ''); } };
        try {
          if (tsWidget === null) tsWidget = window.turnstile.render(ui.ts, { sitekey: SITEKEY, execution: 'execute', appearance: 'interaction-only', callback: take, 'error-callback': function () { take(''); } });
          else window.turnstile.reset(tsWidget);
          window.turnstile.execute(tsWidget);
        } catch (e) { take(''); }
        setTimeout(function () { if (tsResolve === res) take(''); }, 45000);
      });
    });
  }

  // ---------- UI ----------
  function build() {
    host.innerHTML =
      '<div class="adt-ai">' +
      '<p class="abl-help">Describe your shirt art and pick a style. Our AI draws 4 designs in about 30 seconds, with a clear background, ready for your transfer.</p>' +
      '<label class="abl-label" for="AdtAiIdea">Your design</label>' +
      '<div class="adt-ai__box"><textarea id="AdtAiIdea" class="abl-input adt-ai__idea" rows="3" maxlength="' + MAX_DESIGN + '" autocomplete="off" placeholder="e.g. a bulldog in a football helmet" data-adt-ai-idea></textarea>' +
      '<span class="adt-ai__count" data-adt-ai-count aria-hidden="true">0/' + MAX_DESIGN + '</span></div>' +
      '<div class="adt-ai__ex" data-adt-ai-exes><span>Try:</span></div>' +
      '<label class="abl-label" for="AdtAiWords">Words on it (optional)</label>' +
      '<input id="AdtAiWords" class="abl-input" type="text" maxlength="' + MAX_WORDS + '" autocomplete="off" placeholder="e.g. Snohomish Wolf Pack" data-adt-ai-words>' +
      '<div class="abl-label" id="AdtAiStyleLbl">Style</div>' +
      '<div class="adt-ai__chips" role="radiogroup" aria-labelledby="AdtAiStyleLbl" data-adt-ai-styles></div>' +
      '<div class="abl-label" id="AdtAiPlaceLbl">Where it goes</div>' +
      '<div class="adt-ai__chips" role="radiogroup" aria-labelledby="AdtAiPlaceLbl" data-adt-ai-places></div>' +
      '<p class="adt-ai__err" data-adt-ai-err role="alert" hidden></p>' +
      '<div class="adt-ai__gate" data-adt-ai-gate hidden><p class="abl-help">You\'ve used today\'s free tries. Add your email for <span data-adt-ai-gate-n>10</span> tries a day.</p>' +
      '<div class="adt-ai__gaterow"><input class="abl-input" type="email" autocomplete="email" placeholder="you@example.com" aria-label="Your email" data-adt-ai-email><button type="button" class="abl-done" data-adt-ai-email-go>Continue</button></div></div>' +
      '<button type="button" class="adt-ai__go" data-adt-ai-go>' + SPARK + '<span data-adt-ai-go-label>Make 4 designs</span></button>' +
      '<p class="adt-ai__quota" data-adt-ai-quota></p>' +
      '<div class="adt-ai__ts" data-adt-ai-ts></div>' +
      '<div class="adt-ai__results" data-adt-ai-results hidden>' +
      '<p class="abl-help" data-adt-ai-status role="status" aria-live="polite"></p>' +
      '<div class="adt-ai__progress" data-adt-ai-progress hidden><i></i></div>' +
      '<div class="adt-ai__grid" data-adt-ai-grid aria-label="AI designs"></div>' +
      '<button type="button" class="abl-done adt-ai__again" data-adt-ai-again>Try 4 new versions</button>' +
      '<p class="adt-ai__fine">By using a design you confirm it doesn\'t copy anyone else\'s characters, logos or likeness.</p>' +
      '</div>' +
      '<p class="abl-help adt-ai__more">Want to change a design? <a href="' + esc(AI_PAGE) + '" data-adt-ai-page>Open the full AI t-shirt design generator</a>.</p>' +
      '</div>';
    ui.input = $('[data-adt-ai-idea]'); ui.words = $('[data-adt-ai-words]'); ui.count = $('[data-adt-ai-count]'); ui.go = $('[data-adt-ai-go]'); ui.goLabel = $('[data-adt-ai-go-label]');
    ui.quota = $('[data-adt-ai-quota]'); ui.err = $('[data-adt-ai-err]'); ui.gate = $('[data-adt-ai-gate]'); ui.email = $('[data-adt-ai-email]');
    ui.results = $('[data-adt-ai-results]'); ui.status = $('[data-adt-ai-status]'); ui.progress = $('[data-adt-ai-progress]'); ui.grid = $('[data-adt-ai-grid]');
    ui.again = $('[data-adt-ai-again]'); ui.ts = $('[data-adt-ai-ts]');
    var ex = $('[data-adt-ai-exes]');
    EXAMPLES.forEach(function (t) { var b = document.createElement('button'); b.type = 'button'; b.className = 'adt-ai__chip'; b.setAttribute('data-adt-ai-ex', t); b.textContent = t; ex.appendChild(b); });
    var st = $('[data-adt-ai-styles]');
    STYLES.forEach(function (s) { var b = document.createElement('button'); b.type = 'button'; b.className = 'adt-ai__chip'; b.setAttribute('role', 'radio'); b.setAttribute('data-adt-ai-style', s[0]); b.title = s[2]; b.textContent = s[1]; st.appendChild(b); });
    var pl = $('[data-adt-ai-places]');
    PLACES.forEach(function (p) { var b = document.createElement('button'); b.type = 'button'; b.className = 'adt-ai__chip'; b.setAttribute('role', 'radio'); b.setAttribute('data-adt-ai-place', p[0]); b.textContent = p[1]; pl.appendChild(b); });
    setStyle(S.style); setPlace(S.place);
    host.addEventListener('click', onClick);
    ui.input.addEventListener('input', function () { syncCount(); showErr(''); });
    ui.input.addEventListener('keydown', function (e) { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); generate(false); } });
    ui.email.addEventListener('keydown', function (e) { if (e.key === 'Enter') { e.preventDefault(); useEmail(); } });
    syncCount();
  }
  function onClick(e) {
    var b = e.target.closest('button, a'); if (!b || !host.contains(b)) return;
    if (b.hasAttribute('data-adt-ai-ex')) { ui.input.value = b.getAttribute('data-adt-ai-ex'); syncCount(); showErr(''); ui.input.focus(); return; }
    if (b.hasAttribute('data-adt-ai-style')) { setStyle(b.getAttribute('data-adt-ai-style')); return; }
    if (b.hasAttribute('data-adt-ai-place')) { setPlace(b.getAttribute('data-adt-ai-place')); return; }
    if (b.hasAttribute('data-adt-ai-go')) { generate(false); return; }
    if (b.hasAttribute('data-adt-ai-again')) { generate(true); return; }
    if (b.hasAttribute('data-adt-ai-use')) { useDesign(b.getAttribute('data-adt-ai-use')); return; }
    if (b.hasAttribute('data-adt-ai-email-go')) { useEmail(); return; }
    if (b.hasAttribute('data-adt-ai-page')) {
      var t = clean(ui.input.value), q = [];
      if (t) q.push('idea=' + encodeURIComponent(t.slice(0, MAX)));
      q.push('placement=' + encodeURIComponent(S.place));
      b.href = AI_PAGE + (AI_PAGE.indexOf('?') < 0 ? '?' : '&') + q.join('&');
    }
  }
  function setStyle(v) { S.style = v; $$('[data-adt-ai-style]').forEach(function (x) { var on = x.getAttribute('data-adt-ai-style') === v; x.setAttribute('aria-checked', on ? 'true' : 'false'); x.classList.toggle('is-on', on); }); }
  function setPlace(v) { S.place = v; $$('[data-adt-ai-place]').forEach(function (x) { var on = x.getAttribute('data-adt-ai-place') === v; x.setAttribute('aria-checked', on ? 'true' : 'false'); x.classList.toggle('is-on', on); }); }
  function syncCount() { ui.count.textContent = ui.input.value.length + '/' + MAX_DESIGN; }
  function showErr(m) { if (!ui.err) return; ui.err.textContent = m || ''; ui.err.hidden = !m; }
  function setQuota() {
    if (S.ideasLeft == null) return;
    var n = S.ideasLeft;
    ui.quota.textContent = S.hasEmail ? (n > 0 ? n + ' design tr' + (n === 1 ? 'y' : 'ies') + ' left today' : 'No tries left today')
      : (n > 0 ? n + ' free tr' + (n === 1 ? 'y' : 'ies') + ' · no sign-up' : 'Add your email for ' + (S.emailIdeas || 10) + ' tries a day');
    var gn = $('[data-adt-ai-gate-n]'); if (gn) gn.textContent = String(S.emailIdeas || 10);
  }
  function setBusy(on, label) {
    S.busy = on;
    ui.go.disabled = on || S.off; ui.again.disabled = on;
    ui.goLabel.textContent = on ? (label || 'Drawing…') : 'Make 4 designs';
    $$('[data-adt-ai-use]').forEach(function (x) { x.disabled = on; });
  }
  function startLoading() {
    setBusy(true, 'Drawing…');
    ui.results.hidden = false; ui.progress.hidden = false; ui.grid.innerHTML = '';
    for (var i = 0; i < 4; i++) { var c = document.createElement('div'); c.className = 'adt-ai__card is-loading'; c.setAttribute('aria-hidden', 'true'); ui.grid.appendChild(c); }
    var t0 = Date.now(), k = 0, fillEl = ui.progress.firstElementChild;
    ui.status.textContent = LOADING[0];
    clearInterval(S.timer);
    S.timer = setInterval(function () {
      var t = (Date.now() - t0) / 1000;
      fillEl.style.width = Math.min(94, 100 * (1 - Math.exp(-t / 14))) + '%';
      if (Math.floor(t / 6) !== k && k < LOADING.length - 1) { k = Math.min(LOADING.length - 1, Math.floor(t / 6)); ui.status.textContent = LOADING[k]; }
    }, 250);
  }
  function stopLoading() { clearInterval(S.timer); ui.progress.hidden = true; ui.progress.firstElementChild.style.width = '0'; setBusy(false); }
  function renderResults(note) {
    ui.grid.innerHTML = '';
    if (!S.images.length) { ui.results.hidden = true; return; }
    ui.results.hidden = false;
    S.images.forEach(function (im, i) {
      var c = document.createElement('button');
      c.type = 'button'; c.className = 'adt-ai__card' + (im.id === S.selected ? ' is-selected' : '');
      c.setAttribute('data-adt-ai-use', im.id); c.setAttribute('aria-label', 'Use AI design ' + (i + 1));
      c.innerHTML = '<span class="adt-ai__art"><img alt="" decoding="async"></span><span class="adt-ai__use">Use this design</span>';
      var img = c.querySelector('img');
      img.addEventListener('load', function () { c.classList.add('is-in'); });
      img.addEventListener('error', function () { c.classList.add('is-broken'); c.disabled = true; });
      img.src = im.url || (API + '/img/' + im.id);
      ui.grid.appendChild(c);
    });
    ui.status.textContent = note || 'Tap the one you like best to put it on your sheet.';
  }
  function remember() {
    if (!S.ideaId) return;
    var hist = sget('ardorDtfAiHistory'); if (!Array.isArray(hist)) hist = [];
    var entry = { ideaId: S.ideaId, idea: S.idea, desc: S.desc, words: S.words, logo: '', style: S.style, placement: S.place, tee: null, images: S.images.slice(0, 8), selected: S.selected, at: Date.now() };
    sset('ardorDtfAiHistory', [entry].concat(hist.filter(function (h) { return h && h.ideaId !== S.ideaId; })).slice(0, 6));
  }
  function openGate(next) { S.pending = next || null; ui.gate.hidden = false; ui.email.value = email || ''; setTimeout(function () { try { ui.email.focus(); } catch (e) {} }, 60); dl('ardor_dtf_ai_gate_shown', {}); }
  function useEmail() {
    var addr = clean(ui.email.value).toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(addr)) { showErr('Please type a valid email.'); ui.email.focus(); return; }
    showErr('');
    email = addr; lset('wsIdeaEmail', addr); S.hasEmail = true;
    dl('ardor_dtf_ai_email', {});
    quota().then(function () {
      ui.gate.hidden = true;
      var next = S.pending; S.pending = null;
      if (next && (S.ideasLeft == null || S.ideasLeft > 0)) next();
    });
  }
  // the idea text, built like the AI t-shirt page's compose(): design, then the words (never the word "t-shirt")
  function compose() {
    var desc = clean(ui.input.value).replace(/\bt-?shirts?\b/gi, 'design').slice(0, MAX_DESIGN), words = clean(ui.words.value).replace(/"/g, '\'').slice(0, MAX_WORDS);
    var tail = words ? ' The words "' + words + '".' : '';
    var d = desc.replace(/[.\s]+$/, '');
    var out = (d ? d + '.' : '') + tail;
    if (out.length > MAX) out = d.slice(0, Math.max(0, MAX - tail.length - 1)) + '.' + tail;
    S.desc = desc; S.words = words;
    return clean(out);
  }

  // ---------- 4 designs, then the picked one goes on the sheet ----------
  function generate(again) {
    if (S.busy || S.off) return;
    var idea = again && S.idea ? S.idea : compose();
    if (idea.replace(/[^a-z0-9]/gi, '').length < 3) { showErr('Tell us a little more about your design.'); ui.input.focus(); return; }
    showErr('');
    if (!S.hasEmail && S.ideasLeft === 0) { openGate(function () { generate(again); }); return; }
    var prev = { ideaId: S.ideaId, idea: S.idea, images: S.images, selected: S.selected };
    S.idea = idea;
    startLoading();
    botToken().then(function (token) {
      return api('/api/generate', { idea: idea, style: S.style, visitor: visitor, email: S.hasEmail ? email : '', token: token });
    }).then(function (j) {
      stopLoading();
      if (j.ideasLeft != null) S.ideasLeft = j.ideasLeft;
      if (!j.ok || !j.images || !j.images.length) {
        S.ideaId = prev.ideaId; S.idea = prev.idea; S.images = prev.images; S.selected = prev.selected;
        renderResults();
        if (j.error === 'need_email') { S.ideasLeft = 0; setQuota(); openGate(function () { generate(again); }); return; }
        showErr(j.message || 'We couldn\'t draw that one. Please try again.');
        setQuota();
        return;
      }
      S.ideaId = j.ideaId || ''; S.idea = j.idea || idea; S.images = j.images.slice(0, 8); S.selected = null;
      if ('hasEmail' in j) S.hasEmail = !!j.hasEmail;
      setQuota(); renderResults(); remember();
      dl('ardor_dtf_ai_generated', { style: S.style, placement: S.place, again: !!again, from: 'designer' });
    });
  }
  function useDesign(id) {
    if (S.busy) return;
    var im = S.images.filter(function (x) { return x.id === id; })[0], Ly = L();
    if (!im || !/^[0-9A-Za-z_-]{6,80}$/.test(id)) return;
    if (!Ly || !Ly.placeAi) { showErr('Please reload the page and try again.'); return; }
    showErr('');
    S.selected = id; remember();
    $$('[data-adt-ai-use]').forEach(function (x) { var on = x.getAttribute('data-adt-ai-use') === id; x.classList.toggle('is-selected', on); x.classList.toggle('is-placing', on); });
    setBusy(true, 'One moment…');
    ui.status.textContent = 'Putting your design on your sheet…';
    sset('ardorDtfAiPick', { v: 1, ai: id, src: 'sticker', url: API + '/img/' + id, ideaId: S.ideaId, idea: S.idea, style: S.style, placement: S.place, at: Date.now() });
    Ly.placeAi({ id: id, src: 'sticker', placement: S.place, url: im.url || (API + '/img/' + id) }).then(function () {
      setBusy(false);
      $$('.adt-ai__card.is-placing').forEach(function (x) { x.classList.remove('is-placing'); });
      var gang = window.ArdorDtfAPI && window.ArdorDtfAPI.state && window.ArdorDtfAPI.state.mode === 'gang';
      ui.status.textContent = gang ? 'This design is on your sheet. Tap another one to add it too.' : 'This design is on your sheet. Tap another one to swap it.';
      dl('ardor_dtf_ai_design_used', { from: 'designer', placement: S.place, style: S.style });
      if (Ly.closeDrawer && window.matchMedia && window.matchMedia('(max-width: 900px)').matches) Ly.closeDrawer();
    }).catch(function () {
      setBusy(false);
      $$('.adt-ai__card.is-placing').forEach(function (x) { x.classList.remove('is-placing'); });
      ui.status.textContent = 'Tap the one you like best to put it on your sheet.';
      showErr('We couldn\'t load that design. It may have expired. Try another one or make new ones.');
    });
  }

  // ---------- opening the drawer ----------
  function start() {
    if (S.started) return;
    S.started = true;
    email = lget('wsIdeaEmail') || email; S.hasEmail = S.hasEmail || !!email;
    if (!API) { ui.go.disabled = true; showErr('AI design is not available right now. Please try again later.'); return; }
    var st = window.ArdorDtfAPI && window.ArdorDtfAPI.state; if (st && st.place && st.place !== 'youth') setPlace(st.place);
    quota();
    loadTurnstile();
    var last = (sget('ardorDtfAiHistory') || [])[0];
    if (last && last.images && last.images.length && !S.images.length) {
      S.ideaId = last.ideaId || ''; S.idea = String(last.idea || ''); S.images = last.images.slice(0, 8); S.selected = last.selected || null;
      if (last.style) setStyle(last.style);
      ui.input.value = String(last.desc || '').slice(0, MAX_DESIGN); ui.words.value = String(last.words || '').slice(0, MAX_WORDS); syncCount();
      renderResults('Your latest designs. Tap one to put it on your sheet.');
    }
  }
  function boot() {
    var A = window.ArdorDtfAPI, root = document.querySelector('[data-abb-root]');
    host = root && root.querySelector('[data-afa-host]');
    if (!host || !A || host.__adt) return !!(host && host.__adt);
    host.__adt = true;
    var c = A.conf || {};
    API = String(c.ideaApi || 'https://ws-idea-api.dayne-18a.workers.dev').replace(/\/+$/, '');
    SITEKEY = String(c.turnstile || '');
    AI_PAGE = String(c.ideasUrl || AI_PAGE);
    build();
    var panel = host.closest('[data-abl-panel]'), drawer = host.closest('.abl-drawer');
    var check = function () { if (panel && !panel.hidden && drawer && drawer.classList.contains('is-open')) start(); };
    if (window.MutationObserver) {
      if (panel) new MutationObserver(check).observe(panel, { attributes: true, attributeFilter: ['hidden'] });
      if (drawer) new MutationObserver(check).observe(drawer, { attributes: true, attributeFilter: ['class'] });
    }
    check();
    window.ArdorDtfAI = { state: S, quota: quota, generate: generate, use: useDesign, start: start, compose: compose };
    return true;
  }
  if (!boot()) {
    var tries = 0, iv = setInterval(function () { tries++; if (boot() || tries > 200) clearInterval(iv); }, 100);
  }
})();
